'use client';

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Icon from '@/components/ui/AppIcon';
import Image from '@/components/ui/AppImage';
import Hls from 'hls.js';
import LiveActivityBadge from '@/components/community/LiveActivityBadge';

// Mock data for channels with working streaming sources
const mockChannels = [
// New Testing TV Channels with M3U8 Links
{
  id: 8,
  type: 'tv',
  title: 'Freedom Experience TV',
  slug: 'freedom-experience-tv',
  description: 'Experience freedom through faith-based programming and inspirational content',
  logo_url: "https://img.rocket.new/generatedImages/rocket_gen_img_11ef622a6-1766550212056.png",
  alt: 'Freedom Experience TV logo with dove and freedom symbol',
  category: 'Entertainment',
  tags: ['Inspirational', 'Faith', 'Experience'],
  is_live: true,
  now_playing: { title: 'Freedom Hour', show: 'Daily Inspiration', host: 'Freedom Team' },
  sources: [
  { kind: 'hls', url: 'https://panel.ionflixtvs.com:3221/hybrid/play.m3u8', priority: 1, working: true },
  { kind: 'demo', url: 'demo-video-source', priority: 2, working: false }],

  social: { website: 'https://freedomexperiencetv.com' }
},
{
  id: 9,
  type: 'tv',
  title: 'Inspiration TV',
  slug: 'inspiration-tv',
  description: 'Uplifting and inspiring television content to motivate and encourage viewers',
  logo_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1dc2785f1-1766599940928.png",
  alt: 'Inspiration TV logo with sunrise and motivational quotes',
  category: 'Teaching',
  tags: ['Motivational', 'Uplifting', 'Encouragement'],
  is_live: true,
  now_playing: { title: 'Daily Motivation', show: 'Inspiration Hour', host: 'Inspiration Team' },
  sources: [
  { kind: 'hls', url: 'https://panel.ionflixtvs.com:3989/hybrid/play.m3u8', priority: 1, working: true },
  { kind: 'demo', url: 'demo-video-source', priority: 2, working: false }],

  social: { website: 'https://inspirationtv.com' }
},
{
  id: 10,
  type: 'tv',
  title: 'Movie Sphere TV',
  slug: 'movie-sphere-tv',
  description: 'Family-friendly movies and entertainment content in a faith-based environment',
  logo_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1f5059218-1766550212314.png",
  alt: 'Movie Sphere TV logo with film reel and spherical design',
  category: 'Entertainment',
  tags: ['Movies', 'Family-Friendly', 'Entertainment'],
  is_live: true,
  now_playing: { title: 'Classic Cinema', show: 'Movie Night', host: 'Cinema Team' },
  sources: [
  { kind: 'hls', url: 'https://panel.ionflixtvs.com:3782/hybrid/play.m3u8', priority: 1, working: true },
  { kind: 'demo', url: 'demo-video-source', priority: 2, working: false }],

  social: { website: 'https://moviespheretv.com' }
},
{
  id: 11,
  type: 'tv',
  title: 'Love Zone TV',
  slug: 'love-zone-tv',
  description: 'Programs focused on love, relationships, and community in a Christian context',
  logo_url: "https://img.rocket.new/generatedImages/rocket_gen_img_15646c844-1766550210731.png",
  alt: 'Love Zone TV logo with heart symbol and community circle',
  category: 'Lifestyle',
  tags: ['Relationships', 'Community', 'Love'],
  is_live: true,
  now_playing: { title: 'Love Connection', show: 'Community Hour', host: 'Love Team' },
  sources: [
  { kind: 'hls', url: 'https://panel.ionflixtvs.com:3672/hybrid/play.m3u8', priority: 1, working: true },
  { kind: 'demo', url: 'demo-video-source', priority: 2, working: false }],

  social: { website: 'https://lovezonetv.com' }
},
// New Testing Radio Channels with Live Stream URLs
{
  id: 12,
  type: 'radio',
  title: 'Experiencing Christ Radio',
  slug: 'experiencing-christ-radio',
  description: 'Deep spiritual content and worship music to help you experience Christ daily',
  logo_url: "https://img.rocket.new/generatedImages/rocket_gen_img_192fa3948-1766550211065.png",
  alt: 'Experiencing Christ Radio logo with cross and spiritual imagery',
  category: 'Spiritual',
  tags: ['Worship', 'Spiritual Growth', 'Teaching'],
  is_live: true,
  now_playing: { title: 'In Christ Alone', show: 'Morning Devotion', host: 'Pastor Michael' },
  sources: [
  { kind: 'mp3', url: 'https://live.ionradios.com:1115/stream', priority: 1, working: true },
  { kind: 'demo', url: 'demo-audio-source', priority: 2, working: false }],

  social: { website: 'https://experiencingchristradio.com' }
},
{
  id: 13,
  type: 'radio',
  title: 'Freedom Experience Radio',
  slug: 'freedom-experience-radio',
  description: 'Celebrating freedom in Christ through uplifting music and powerful testimonies',
  logo_url: "https://img.rocket.new/generatedImages/rocket_gen_img_19b07544f-1766550211235.png",
  alt: 'Freedom Experience Radio logo with dove and liberation symbols',
  category: 'Inspirational',
  tags: ['Freedom', 'Testimonies', 'Liberation'],
  is_live: true,
  now_playing: { title: 'Free Indeed', show: 'Freedom Hour', host: 'Grace Williams' },
  sources: [
  { kind: 'mp3', url: 'https://live.ionradios.com:1045/stream', priority: 1, working: true },
  { kind: 'demo', url: 'demo-audio-source', priority: 2, working: false }],

  social: { website: 'https://freedomexperienceradio.com' }
},
{
  id: 14,
  type: 'radio',
  title: 'Inspiration Radio',
  slug: 'inspiration-radio',
  description: 'Daily inspiration through Christian music, messages, and encouraging stories',
  logo_url: "https://img.rocket.new/generatedImages/rocket_gen_img_156718302-1766599941863.png",
  alt: 'Inspiration Radio logo with sunrise and inspiring quotes',
  category: 'Inspirational',
  tags: ['Inspiration', 'Encouragement', 'Motivation'],
  is_live: true,
  now_playing: { title: 'What A Beautiful Name', show: 'Daily Dose', host: 'Sarah Hope' },
  sources: [
  { kind: 'mp3', url: 'https://live.ionradios.com:1505/stream', priority: 1, working: true },
  { kind: 'demo', url: 'demo-audio-source', priority: 2, working: false }],

  social: { website: 'https://inspirationradio.com' }
},
{
  id: 15,
  type: 'radio',
  title: '24/7 Prayer Radio',
  slug: '247-prayer-radio',
  description: 'Continuous prayer and worship music for 24/7 spiritual connection and peace',
  logo_url: "https://img.rocket.new/generatedImages/rocket_gen_img_144331ab7-1766550211084.png",
  alt: '24/7 Prayer Radio logo with praying hands and clock symbolizing continuous prayer',
  category: 'Prayer',
  tags: ['Prayer', '24/7', 'Worship', 'Peace'],
  is_live: true,
  now_playing: { title: 'The Prayer', show: 'Night Watch', host: 'Prayer Team' },
  sources: [
  { kind: 'mp3', url: 'https://live.ionradios.com:1435', priority: 1, working: true },
  { kind: 'demo', url: 'demo-audio-source', priority: 2, working: false }],

  social: { website: 'https://247prayerradio.com' }
},
{
  id: 16,
  type: 'radio',
  title: 'Gospel DJs Mix',
  slug: 'gospel-djs-mix',
  description: 'High-energy gospel music mixed by top Christian DJs for an uplifting experience',
  logo_url: "https://img.rocket.new/generatedImages/rocket_gen_img_1ca070950-1766550210758.png",
  alt: 'Gospel DJs Mix logo with DJ turntables and gospel music elements',
  category: 'Music',
  tags: ['DJ Mix', 'High Energy', 'Gospel', 'Contemporary'],
  is_live: true,
  now_playing: { title: 'Victory Dance Mix', show: 'DJ Gospel Vibes', host: 'DJ Praise' },
  sources: [
  { kind: 'mp3', url: 'https://live.ionradios.com:5025/stream', priority: 1, working: true },
  { kind: 'demo', url: 'demo-audio-source', priority: 2, working: false }],

  social: { website: 'https://gospeldjsmix.com' }
}];


const mockShows = [];

const mockSchedule = [];

// Channel Card Component
const ChannelCard = ({ channel, onPlay, onOpenDetail }) => {
  const [liveViewers, setLiveViewers] = useState(0);
  const [newViewers, setNewViewers] = useState(0);
  const upcomingShows = mockShows?.filter((show) => show?.channel_id === channel?.id)?.slice(0, 3);

  // Simulate live viewer counts
  useEffect(() => {
    if (channel?.is_live) {
      // Initial viewer count
      setLiveViewers(Math.floor(Math.random() * 500) + 100);

      // Update viewer count periodically
      const interval = setInterval(() => {
        const change = Math.floor(Math.random() * 20) - 10;
        setLiveViewers((prev) => Math.max(0, prev + change));
        if (change > 0) {
          setNewViewers(change);
          setTimeout(() => setNewViewers(0), 2000);
        }
      }, 8000);

      return () => clearInterval(interval);
    }
  }, [channel?.is_live]);

  return (
    <div className="bg-card rounded-xl shadow-lg hover:shadow-xl stream-card-hover border border-border overflow-hidden">
      <div className="relative">
        <Image
          src={channel?.logo_url}
          alt={channel?.alt}
          width={400}
          height={200}
          className="w-full h-48 object-cover" />

        {channel?.is_live &&
        <div className="absolute top-4 left-4 z-10">
            <LiveActivityBadge
            type="viewers"
            count={liveViewers}
            showPulse={true}
            showNewIndicator={newViewers > 0}
            newCount={newViewers}
            variant="live"
            size="md"
            label="LIVE" />

          </div>
        }
        <div className="absolute top-4 right-4">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
          channel?.type === 'radio' ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'}`
          }>
            {channel?.type?.toUpperCase()}
          </span>
        </div>
      </div>
      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-1">{channel?.title}</h3>
            <p className="text-sm text-muted-foreground">{channel?.category}</p>
          </div>
        </div>

        {channel?.is_live && channel?.now_playing &&
        <div className="mb-4 p-3 bg-surface rounded-lg">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <Icon name="MusicalNoteIcon" size={16} />
              <span>Now Playing</span>
            </div>
            <p className="font-medium text-foreground">{channel?.now_playing?.title}</p>
            <p className="text-sm text-muted-foreground">
              {channel?.now_playing?.show} • {channel?.now_playing?.host}
            </p>
          </div>
        }

        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
          {channel?.description}
        </p>

        <div className="flex flex-wrap gap-2 mb-4">
          {channel?.tags?.map((tag, index) =>
          <span
            key={index}
            className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">

              {tag}
            </span>
          )}
        </div>

        {upcomingShows?.length > 0 &&
        <div className="mb-4">
            <h4 className="text-sm font-medium text-foreground mb-2">Up Next</h4>
            <div className="space-y-1">
              {upcomingShows?.map((show) =>
            <div key={show?.id} className="text-xs text-muted-foreground">
                  {show?.title} • {show?.host}
                </div>
            )}
            </div>
          </div>
        }

        <div className="flex gap-3">
          <button
            onClick={() => onPlay(channel)}
            className="flex-1 flex items-center justify-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 contemplative-transition">

            <Icon name="PlayIcon" size={16} />
            Play
          </button>
          <button
            onClick={() => onOpenDetail(channel)}
            className="px-4 py-2 border border-border rounded-lg hover:bg-surface contemplative-transition">

            <Icon name="InformationCircleIcon" size={16} />
          </button>
        </div>
      </div>
    </div>);

};

ChannelCard.propTypes = {
  channel: PropTypes?.object?.isRequired,
  onPlay: PropTypes?.func?.isRequired,
  onOpenDetail: PropTypes?.func?.isRequired
};

// Enhanced Video Modal Component with HLS.js Support
const VideoModal = ({ channel, isOpen, onClose }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [videoError, setVideoError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [streamInfo, setStreamInfo] = useState(null);

  useEffect(() => {
    if (!isOpen || !channel || !videoRef?.current) return;

    const initializeStream = async () => {
      setVideoError(null);
      setIsLoading(true);
      setStreamInfo(null);

      const workingSource = channel?.sources?.find((source) => source?.working);
      if (!workingSource) {
        setIsLoading(false);
        setVideoError('No streaming source available');
        return;
      }

      const videoElement = videoRef?.current;
      const streamUrl = workingSource?.url;

      // Log stream attempt for debugging
      console.log('🎥 Attempting to play stream:', {
        channel: channel?.title,
        url: streamUrl,
        type: workingSource?.kind
      });

      // Clean up existing HLS instance
      if (hlsRef?.current) {
        hlsRef?.current?.destroy();
        hlsRef.current = null;
      }

      try {
        // Check if browser supports HLS natively (Safari)
        if (videoElement?.canPlayType('application/vnd.apple.mpegurl')) {
          console.log('✅ Using native HLS support (Safari)');
          videoElement.src = streamUrl;

          // Add CORS attributes for better compatibility
          videoElement.crossOrigin = 'anonymous';
          videoElement?.load();

          setStreamInfo({
            method: 'Native HLS',
            browser: 'Safari/iOS',
            status: 'Connecting...'
          });

          const playPromise = videoElement?.play();
          if (playPromise !== undefined) {
            playPromise?.
            then(() => {
              setIsLoading(false);
              setVideoError(null);
              setStreamInfo({
                method: 'Native HLS',
                browser: 'Safari/iOS',
                status: 'Playing'
              });
              console.log('✅ Stream playing successfully (Native)');
            })?.
            catch((error) => {
              console.warn('⚠️ Autoplay prevented:', error);
              setIsLoading(false);
              setStreamInfo({
                method: 'Native HLS',
                browser: 'Safari/iOS',
                status: 'Ready (Click to play)'
              });
            });
          }
        } else if (Hls?.isSupported()) {
          // Use HLS.js for browsers without native HLS support
          console.log('✅ Using HLS.js for stream playback');

          const hls = new Hls({
            debug: false,
            enableWorker: true,
            // Live streaming optimized buffer settings
            maxBufferLength: 60,
            maxMaxBufferLength: 120,
            backBufferLength: 30,
            maxBufferSize: 100 * 1000 * 1000,
            maxBufferHole: 0.5,
            highBufferWatchdogPeriod: 3,
            nudgeOffset: 0.1,
            nudgeMaxRetry: 5,
            maxFragLookUpTolerance: 0.25,
            liveSyncDurationCount: 5,
            liveMaxLatencyDurationCount: 10,
            liveDurationInfinity: false,
            liveBackBufferLength: 60,
            enableSoftwareAES: true,
            manifestLoadingTimeOut: 15000,
            manifestLoadingMaxRetry: 3,
            manifestLoadingRetryDelay: 1000,
            manifestLoadingMaxRetryTimeout: 64000,
            levelLoadingTimeOut: 15000,
            levelLoadingMaxRetry: 6,
            levelLoadingRetryDelay: 1000,
            levelLoadingMaxRetryTimeout: 64000,
            fragLoadingTimeOut: 25000,
            fragLoadingMaxRetry: 8,
            fragLoadingRetryDelay: 1000,
            fragLoadingMaxRetryTimeout: 64000,
            startFragPrefetch: true,
            testBandwidth: true,
            progressive: false,
            fpsDroppedMonitoringPeriod: 5000,
            fpsDroppedMonitoringThreshold: 0.2,
            appendErrorMaxRetry: 5,
            xhrSetup: function (xhr, url) {
              xhr.withCredentials = false;
            },
            fetchSetup: function (context, initParams) {
              initParams.mode = 'cors';
              initParams.credentials = 'omit';
              return initParams;
            }
          });

          hlsRef.current = hls;

          setStreamInfo({
            method: 'HLS.js',
            browser: 'Chrome/Firefox/Edge',
            status: 'Initializing...'
          });

          // Load source
          hls?.loadSource(streamUrl);
          hls?.attachMedia(videoElement);

          // Handle HLS manifest parsed
          hls?.on(Hls?.Events?.MANIFEST_PARSED, (event, data) => {
            console.log('✅ HLS manifest parsed successfully', data);
            setStreamInfo({
              method: 'HLS.js',
              browser: 'Chrome/Firefox/Edge',
              status: 'Manifest loaded',
              levels: data?.levels?.length || 0,
              firstLevel: data?.firstLevel
            });

            const playPromise = videoElement?.play();
            if (playPromise !== undefined) {
              playPromise?.
              then(() => {
                setIsLoading(false);
                setVideoError(null);
                setStreamInfo((prev) => ({
                  ...prev,
                  status: 'Playing',
                  currentLevel: hls?.currentLevel
                }));
                console.log('✅ Stream playing successfully (HLS.js)');
              })?.
              catch((error) => {
                console.warn('⚠️ Autoplay prevented:', error);
                setIsLoading(false);
                setStreamInfo((prev) => ({
                  ...prev,
                  status: 'Ready (Click to play)'
                }));
              });
            }
          });

          // Handle level switching
          hls?.on(Hls?.Events?.LEVEL_SWITCHED, (event, data) => {
            console.log('📊 Quality level switched:', data?.level);
            setStreamInfo((prev) => ({
              ...prev,
              currentLevel: data?.level
            }));
          });

          // Handle fragment loading
          hls?.on(Hls?.Events?.FRAG_LOADED, (event, data) => {
            console.log('📦 Fragment loaded:', {
              duration: data?.frag?.duration,
              level: data?.frag?.level
            });
          });

          // Enhanced error handling
          hls?.on(Hls?.Events?.ERROR, (event, data) => {
            console.error('❌ HLS Error:', {
              type: data?.type,
              details: data?.details,
              fatal: data?.fatal,
              url: data?.url,
              response: data?.response
            });

            if (data?.fatal) {
              switch (data?.type) {
                case Hls?.ErrorTypes?.NETWORK_ERROR:
                  console.error('🌐 Network error details:', data);
                  setVideoError(
                    `Network error: ${data?.details || 'Cannot reach stream server'}\n` +
                    `URL: ${streamUrl}\n` +
                    `This could be due to:\n` +
                    `• CORS restrictions on the server\n` +
                    `• Server is offline or unreachable\n` +
                    `• Firewall blocking the connection\n` +
                    `• Invalid stream URL`
                  );
                  setIsLoading(false);

                  // Try to recover from network errors
                  console.log('🔄 Attempting network recovery...');
                  setTimeout(() => {
                    hls?.startLoad();
                  }, 3000);
                  break;

                case Hls?.ErrorTypes?.MEDIA_ERROR:
                  console.error('🎬 Media error details:', data);
                  setVideoError('Media decoding error - trying to recover...');

                  // Try to recover from media errors
                  console.log('🔄 Attempting media recovery...');
                  hls?.recoverMediaError();
                  break;

                default:
                  console.error('💥 Fatal streaming error:', data);
                  setVideoError(
                    `Fatal streaming error: ${data?.details || 'Unknown error'}\n` +
                    `Type: ${data?.type}\n` +
                    `The stream cannot be played with current browser settings.`
                  );
                  setIsLoading(false);
                  hls?.destroy();
                  break;
              }
            } else {
              // Non-fatal errors
              console.warn('⚠️ Non-fatal HLS error:', data);
            }
          });

          // Monitor buffer levels
          hls?.on(Hls?.Events?.BUFFER_APPENDED, (event, data) => {
            console.log('📊 Buffer status:', {
              timeRanges: data?.timeRanges
            });
          });

        } else {
          // Browser doesn't support HLS
          console.error('❌ Browser does not support HLS streaming');
          setIsLoading(false);
          setVideoError(
            'Your browser does not support HLS streaming.\n\n' +
            'Please try:\n' + '• Using a modern browser (Chrome, Firefox, Safari, Edge)\n' + '• Updating your current browser to the latest version\n' + '• Enabling JavaScript if it\'s disabled\n\n' + 'Supported browsers:\n' + '✓ Chrome 34+\n' + '✓ Firefox 42+\n' + '✓ Safari (native HLS)\n' + '✓ Edge 79+'
          );
        }
      } catch (error) {
        console.error('💥 Unexpected error during stream initialization:', error);
        setVideoError(
          `Unexpected error: ${error?.message || 'Failed to initialize stream'}\n\n` +
          'Please try:\n' + '• Refreshing the page\n' + '• Clearing browser cache\n' + '• Using a different browser'
        );
        setIsLoading(false);
      }
    };

    initializeStream();

    // Cleanup function
    return () => {
      if (hlsRef?.current) {
        console.log('🧹 Cleaning up HLS instance');
        hlsRef?.current?.destroy();
        hlsRef.current = null;
      }
    };
  }, [isOpen, channel]);

  const handleVideoError = (e) => {
    const error = e?.target?.error;
    console.error('❌ Video element error:', {
      code: error?.code,
      message: error?.message,
      MEDIA_ERR_ABORTED: error?.MEDIA_ERR_ABORTED,
      MEDIA_ERR_NETWORK: error?.MEDIA_ERR_NETWORK,
      MEDIA_ERR_DECODE: error?.MEDIA_ERR_DECODE,
      MEDIA_ERR_SRC_NOT_SUPPORTED: error?.MEDIA_ERR_SRC_NOT_SUPPORTED
    });

    let errorMessage = 'Unable to play stream';
    let errorDetails = '';

    if (error) {
      switch (error?.code) {
        case error?.MEDIA_ERR_NETWORK:
          errorMessage = 'Network error';
          errorDetails = 'Failed to load the stream due to network issues.\n' + 'Please check your internet connection and try again.';
          break;
        case error?.MEDIA_ERR_DECODE:
          errorMessage = 'Decoding error';
          errorDetails = 'The video format is not supported or the stream is corrupted.\n' + 'This stream may not be compatible with your browser.';
          break;
        case error?.MEDIA_ERR_SRC_NOT_SUPPORTED:
          errorMessage = 'Source not supported';
          errorDetails = 'The stream format is not supported by your browser.\n\n' + 'This could be because:\n' + '• The stream server has CORS restrictions\n' + '• The stream URL is invalid or expired\n' + '• Your browser doesn\'t support this video format\n\n' +
          'Try using a different browser or contact support.';
          break;
        case error?.MEDIA_ERR_ABORTED:
          errorMessage = 'Playback aborted';
          errorDetails = 'Video playback was interrupted.\n' + 'Please try playing again.';
          break;
        default:
          errorMessage = 'Unknown error';
          errorDetails = 'An unexpected error occurred during playback.\n' + 'Please refresh the page and try again.';
      }
    }

    setVideoError(`${errorMessage}\n\n${errorDetails}`);
    setIsLoading(false);
  };

  const handleVideoCanPlay = () => {
    setIsLoading(false);
    setVideoError(null);
  };

  const handleLoadStart = () => {
    setIsLoading(true);
  };

  const handleRetry = async () => {
    console.log('🔄 Manual retry initiated');
    setVideoError(null);
    setIsLoading(true);
    setStreamInfo(null);

    const workingSource = channel?.sources?.find((source) => source?.working);
    if (!workingSource || !videoRef?.current) {
      setVideoError('No valid stream source available');
      setIsLoading(false);
      return;
    }

    const videoElement = videoRef?.current;
    const streamUrl = workingSource?.url;

    // Clean up existing HLS instance
    if (hlsRef?.current) {
      hlsRef?.current?.destroy();
      hlsRef.current = null;
    }

    try {
      // Retry with HLS.js
      if (Hls?.isSupported()) {
        console.log('🔄 Retrying with HLS.js...');
        const hls = new Hls({
          debug: false,
          enableWorker: true,
          lowLatencyMode: true,
          maxBufferLength: 30,
          maxMaxBufferLength: 60,
          xhrSetup: function (xhr) {
            xhr.withCredentials = false;
          }
        });

        hlsRef.current = hls;
        hls?.loadSource(streamUrl);
        hls?.attachMedia(videoElement);

        hls?.on(Hls?.Events?.MANIFEST_PARSED, () => {
          videoElement?.play()?.then(() => {
            console.log('✅ Retry successful');
            setIsLoading(false);
            setVideoError(null);
          })?.catch((error) => {
            console.error('❌ Retry failed:', error);
            setVideoError('Unable to play stream after retry.\n' + 'The stream may be temporarily unavailable.');
            setIsLoading(false);
          });
        });

        hls?.on(Hls?.Events?.ERROR, (event, data) => {
          if (data?.fatal) {
            console.error('❌ Retry error:', data);
            setVideoError('Stream connection failed during retry.\n' + 'The stream server may be offline or blocking connections.');
            setIsLoading(false);
          }
        });
      } else if (videoElement?.canPlayType('application/vnd.apple.mpegurl')) {
        console.log('🔄 Retrying with native HLS (Safari)...');
        videoElement.src = streamUrl;
        videoElement.crossOrigin = 'anonymous';
        videoElement?.load();

        videoElement?.play()?.then(() => {
          console.log('✅ Retry successful (Native)');
          setIsLoading(false);
          setVideoError(null);
        })?.catch((error) => {
          console.error('❌ Retry failed:', error);
          setVideoError('Unable to play stream after retry.');
          setIsLoading(false);
        });
      } else {
        setVideoError('Your browser does not support video streaming.\n' +
        'Please try a different browser.');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('💥 Error during retry:', error);
      setVideoError('Failed to retry stream playback.\n' + 'Please refresh the page and try again.');
      setIsLoading(false);
    }
  };

  if (!isOpen || !channel) return null;

  const workingSource = channel?.sources?.find((source) => source?.working);

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-background rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="relative">
          <div className="aspect-video bg-black flex items-center justify-center">
            {!workingSource ?
            <div className="text-center text-white p-8">
                <Icon name="ExclamationCircleIcon" size={64} className="mx-auto mb-4 text-orange-400" />
                <h3 className="text-xl font-semibold mb-2">No Stream Available</h3>
                <p className="text-white/70">This channel does not have a valid streaming source configured.</p>
              </div> :

            <div className="w-full h-full relative">
                {isLoading &&
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
                    <div className="text-center">
                      <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                      <p className="text-white font-medium">Connecting to stream...</p>
                      {streamInfo &&
                  <div className="mt-4 text-sm text-white/80 space-y-1">
                          <p>Method: {streamInfo?.method}</p>
                          <p>Status: {streamInfo?.status}</p>
                        </div>
                  }
                    </div>
                  </div>
              }
                {videoError &&
              <div className="absolute inset-0 flex items-center justify-center text-center text-white p-6 bg-black/90 z-10 overflow-y-auto">
                    <div className="max-w-md">
                      <Icon name="ExclamationCircleIcon" size={48} className="mx-auto mb-3 text-orange-400" />
                      <p className="text-lg font-semibold mb-3">Playback Error</p>
                      <div className="text-sm text-white/90 mb-6 whitespace-pre-line text-left bg-black/50 p-4 rounded-lg max-h-64 overflow-y-auto">
                        {videoError}
                      </div>
                      <div className="flex gap-3 justify-center">
                        <button
                      onClick={handleRetry}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 flex items-center gap-2">
                          <Icon name="ArrowPathIcon" size={16} />
                          Retry Connection
                        </button>
                        <button
                      onClick={() => {
                        setVideoError(null);
                        setStreamInfo(null);
                      }}
                      className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20">
                          Dismiss
                        </button>
                      </div>
                      {streamInfo &&
                  <div className="mt-4 text-xs text-white/60 text-left bg-black/30 p-3 rounded">
                          <p className="font-medium mb-2">Technical Details:</p>
                          <p>Method: {streamInfo?.method}</p>
                          <p>Browser: {streamInfo?.browser}</p>
                          <p>Stream URL: {workingSource?.url?.substring(0, 50)}...</p>
                        </div>
                  }
                    </div>
                  </div>
              }
                <video
                ref={videoRef}
                className="w-full h-full"
                controls
                playsInline


                poster={channel?.logo_url}
                onError={handleVideoError}
                onCanPlay={handleVideoCanPlay}
                onLoadStart={handleLoadStart}
                onPlaying={() => {
                  console.log('✅ Video playing event');
                  setStreamInfo((prev) => prev ? { ...prev, status: 'Playing' } : null);
                }}
                onWaiting={() => {
                  console.log('⏳ Video buffering...');
                  setStreamInfo((prev) => prev ? { ...prev, status: 'Buffering...' } : null);
                }}>
                  Your browser does not support video playback.
                </video>
                {/* Stream Info Overlay (Debug Mode) */}
                {streamInfo && !videoError && !isLoading &&
              <div className="absolute bottom-16 left-4 bg-black/70 text-white text-xs p-2 rounded">
                    <p>📊 {streamInfo?.method}</p>
                    <p>Status: {streamInfo?.status}</p>
                    {streamInfo?.levels > 0 &&
                <p>Quality: {streamInfo?.currentLevel + 1}/{streamInfo?.levels}</p>
                }
                  </div>
              }
              </div>
            }
          </div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 contemplative-transition z-20">
            <Icon name="XMarkIcon" size={20} />
          </button>
        </div>

        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-xl font-semibold text-foreground">{channel?.title}</h3>
              <p className="text-muted-foreground">{channel?.description}</p>
            </div>
            {channel?.is_live &&
            <div className="flex items-center gap-2 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                <div className="w-2 h-2 bg-white rounded-full live-indicator"></div>
                LIVE
              </div>
            }
          </div>

          {channel?.is_live && channel?.now_playing &&
          <div className="mb-4 p-4 bg-surface rounded-lg">
              <h4 className="font-medium text-foreground mb-2">Now Playing</h4>
              <p className="text-foreground">{channel?.now_playing?.title}</p>
              <p className="text-sm text-muted-foreground">
                {channel?.now_playing?.show} • {channel?.now_playing?.host}
              </p>
            </div>
          }

          {/* Troubleshooting Tips */}
          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="InformationCircleIcon" size={20} className="text-blue-600 dark:text-blue-400 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                  Streaming Tips
                </h4>
                <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                  <li>• Ensure stable internet connection (3+ Mbps recommended)</li>
                  <li>• Try refreshing if stream doesn't load within 10 seconds</li>
                  <li>• Use latest version of Chrome, Firefox, Safari, or Edge</li>
                  <li>• Check if browser allows video autoplay in settings</li>
                  <li>• Disable VPN/proxy if experiencing connection issues</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Live Chat Interface */}
          <div className="mt-6 p-4 border-2 border-dashed border-border rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="ChatBubbleLeftRightIcon" size={20} className="text-muted-foreground" />
              <h4 className="font-medium text-foreground">Live Chat</h4>
            </div>
            <div className="space-y-2 mb-3 max-h-32 overflow-y-auto">
              <div className="text-sm">
                <span className="font-medium text-primary">John_D:</span>
                <span className="text-foreground ml-2">Great message today! 🙏</span>
              </div>
              <div className="text-sm">
                <span className="font-medium text-primary">Sarah_M:</span>
                <span className="text-foreground ml-2">Praying along with you all</span>
              </div>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Type a message..."
                className="flex-1 px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent" />
              <button className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 contemplative-transition">
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>);

};

VideoModal.propTypes = {
  channel: PropTypes?.object,
  isOpen: PropTypes?.bool?.isRequired,
  onClose: PropTypes?.func?.isRequired
};

// Enhanced Sticky Player Component with Real Audio Streaming
const StickyPlayer = ({ currentChannel, isPlaying, onTogglePlay, onClose }) => {
  const audioRef = useRef(null);
  const [audioError, setAudioError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentMetadata, setCurrentMetadata] = useState(null);
  const [trackHistory, setTrackHistory] = useState([]);
  const [albumArt, setAlbumArt] = useState(null);
  const [showTrackHistory, setShowTrackHistory] = useState(false);
  const [currentListeners, setCurrentListeners] = useState(0);
  const [newListeners, setNewListeners] = useState(0);

  // Simulate metadata fetching for radio streams
  const fetchStreamMetadata = async (streamUrl) => {
    try {
      const simulatedMetadata = {
        title: `${currentChannel?.now_playing?.title || 'Current Track'}`,
        artist: currentChannel?.now_playing?.host || 'Unknown Artist',
        album: currentChannel?.now_playing?.show || 'Live Stream',
        duration: Math.floor(Math.random() * 300) + 180,
        position: Math.floor(Math.random() * 120),
        artwork: `https://img.rocket.new/generatedImages/album_art_${Math.floor(Math.random() * 1000)}.png`,
        genre: currentChannel?.category || 'Christian',
        bitrate: '128 kbps',
        listeners: Math.floor(Math.random() * 5000) + 100
      };

      setCurrentMetadata(simulatedMetadata);
      setAlbumArt(simulatedMetadata?.artwork);
      setCurrentListeners(simulatedMetadata?.listeners);

      setTrackHistory((prev) => {
        const newHistory = [{
          ...simulatedMetadata,
          timestamp: new Date()?.toLocaleTimeString(),
          id: Date.now()
        }, ...prev?.slice(0, 9)];
        return newHistory;
      });

    } catch (error) {
      console.log('Metadata fetch simulation:', error);
    }
  };

  useEffect(() => {
    let metadataInterval;

    if (isPlaying && currentChannel && currentChannel?.type === 'radio') {
      fetchStreamMetadata(currentChannel?.sources?.[0]?.url);

      metadataInterval = setInterval(() => {
        fetchStreamMetadata(currentChannel?.sources?.[0]?.url);
      }, 30000);
    }

    return () => {
      if (metadataInterval) {
        clearInterval(metadataInterval);
      }
    };
  }, [isPlaying, currentChannel]);

  // Simulate listener count changes
  useEffect(() => {
    if (isPlaying && currentListeners > 0) {
      const interval = setInterval(() => {
        const change = Math.floor(Math.random() * 10) - 5;
        setCurrentListeners((prev) => Math.max(0, prev + change));
        if (change > 0) {
          setNewListeners(change);
          setTimeout(() => setNewListeners(0), 2000);
        }
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [isPlaying, currentListeners]);

  useEffect(() => {
    if (currentChannel && currentChannel?.type === 'radio') {
      setAudioError(null);
      setIsLoading(true);

      if (audioRef?.current) {
        const workingSource = currentChannel?.sources?.find((source) => source?.working);

        if (workingSource) {
          audioRef.current.src = workingSource?.url;
          audioRef?.current?.load();

          if (isPlaying) {
            audioRef?.current?.play()?.then(() => {
              setIsLoading(false);
              fetchStreamMetadata(workingSource?.url);
            })?.catch((error) => {
              console.error('Audio playback error:', error);
              setAudioError(`Playback error: ${error?.message}`);
              setIsLoading(false);
              fetchStreamMetadata('demo');
            });
          } else {
            audioRef?.current?.pause();
            setIsLoading(false);
          }
        } else {
          setAudioError('No valid audio source available');
          setIsLoading(false);
        }
      }
    }
  }, [currentChannel, isPlaying]);

  const handleAudioError = (e) => {
    const error = e?.target?.error;
    console.error('Audio streaming error:', error);

    let errorMessage = 'Unable to play audio stream';
    if (error) {
      switch (error?.code) {
        case error?.MEDIA_ERR_NETWORK:
          errorMessage = 'Network error - check connection';
          break;
        case error?.MEDIA_ERR_DECODE:
          errorMessage = 'Audio format not supported';
          break;
        case error?.MEDIA_ERR_SRC_NOT_SUPPORTED:
          errorMessage = 'Stream source not supported';
          break;
        default:
          errorMessage = 'Playback error occurred';
      }
    }

    setAudioError(errorMessage);
    setIsLoading(false);
  };

  const handleAudioCanPlay = () => {
    setIsLoading(false);
    setAudioError(null);
  };

  if (!currentChannel || currentChannel?.type !== 'radio') return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 sticky-player">
      <audio
        ref={audioRef}
        preload="auto"
        onError={handleAudioError}
        onCanPlay={handleAudioCanPlay} />
      <div className="bg-card border-t border-border shadow-xl">
        <div className="flex items-center justify-between p-4 max-w-7xl mx-auto">
          <div className="flex items-center gap-4 flex-1">
            <div className="relative">
              <Image
                src={albumArt || currentChannel?.logo_url}
                alt={`${currentChannel?.title} album art or station logo`}
                width={64}
                height={64}
                className="rounded-lg shadow-md" />
              {isPlaying && !audioError &&
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                </div>
              }
            </div>

            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-foreground truncate">
                {currentMetadata?.title || currentChannel?.title}
              </h4>
              
              {currentMetadata ?
              <div className="space-y-1">
                  <p className="text-sm text-muted-foreground truncate">
                    {currentMetadata?.artist} • {currentMetadata?.album}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span>{currentMetadata?.genre}</span>
                    <span>{currentMetadata?.bitrate}</span>
                    <LiveActivityBadge
                    type="users"
                    count={currentListeners}
                    showNewIndicator={newListeners > 0}
                    newCount={newListeners}
                    size="sm"
                    variant="success"
                    label="listeners" />

                  </div>
                </div> :

              audioError ?
              <div>
                    <p className="text-xs text-orange-600 truncate">{audioError}</p>
                    <button
                  onClick={() => {
                    setAudioError(null);
                    if (audioRef?.current) {
                      audioRef?.current?.load();
                      audioRef?.current?.play()?.catch(console.error);
                    }
                  }}
                  className="text-xs text-primary hover:text-primary/80 mt-1">
                      Retry Connection
                    </button>
                  </div> :

              <p className="text-sm text-muted-foreground">
                    {isLoading ? 'Connecting to stream...' : 'Ready to play'}
                  </p>

              }

              {currentMetadata && currentMetadata?.duration > 0 &&
              <div className="mt-2">
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                    <span>{Math.floor(currentMetadata?.position / 60)}:{(currentMetadata?.position % 60)?.toString()?.padStart(2, '0')}</span>
                    <span>{Math.floor(currentMetadata?.duration / 60)}:{(currentMetadata?.duration % 60)?.toString()?.padStart(2, '0')}</span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-1">
                    <div
                    className="bg-primary h-1 rounded-full transition-all duration-1000"
                    style={{ width: `${currentMetadata?.position / currentMetadata?.duration * 100}%` }} />
                  </div>
                </div>
              }
            </div>
          </div>

          <div className="flex items-center gap-4">
            {trackHistory?.length > 0 &&
            <button
              onClick={() => setShowTrackHistory(!showTrackHistory)}
              className="p-2 text-muted-foreground hover:text-foreground contemplative-transition"
              title="Track History">
                <Icon name="ClockIcon" size={20} />
              </button>
            }

            {isLoading &&
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            }
            
            <button
              onClick={onTogglePlay}
              disabled={isLoading}
              className="flex items-center justify-center w-12 h-12 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 contemplative-transition disabled:opacity-50">
              <Icon name={isPlaying ? 'PauseIcon' : 'PlayIcon'} size={24} />
            </button>
            
            <button
              onClick={onClose}
              className="p-2 text-muted-foreground hover:text-foreground contemplative-transition">
              <Icon name="XMarkIcon" size={20} />
            </button>
          </div>
        </div>

        {showTrackHistory && trackHistory?.length > 0 &&
        <div className="border-t border-border bg-surface/50 p-4 max-h-64 overflow-y-auto">
            <div className="max-w-7xl mx-auto">
              <h5 className="font-medium text-foreground mb-3 flex items-center gap-2">
                <Icon name="ClockIcon" size={16} />
                Recently Played
              </h5>
              <div className="space-y-2">
                {trackHistory?.map((track) =>
              <div key={track?.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-background/50">
                    <div className="w-8 h-8 bg-muted rounded flex items-center justify-center">
                      <Icon name="MusicalNoteIcon" size={14} className="text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{track?.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{track?.artist}</p>
                    </div>
                    <span className="text-xs text-muted-foreground">{track?.timestamp}</span>
                  </div>
              )}
              </div>
            </div>
          </div>
        }
      </div>
    </div>);

};

StickyPlayer.propTypes = {
  currentChannel: PropTypes?.object,
  isPlaying: PropTypes?.bool?.isRequired,
  onTogglePlay: PropTypes?.func?.isRequired,
  onClose: PropTypes?.func?.isRequired
};

// Weekly Schedule Component
const WeeklySchedule = ({ channelId }) => {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const hours = Array.from({ length: 24 }, (_, i) => `${i?.toString()?.padStart(2, '0')}:00`);

  const scheduleForChannel = mockSchedule?.filter((s) => s?.channel_id === channelId);
  const currentHour = new Date()?.getHours();
  const currentDay = days?.[new Date()?.getDay() - 1] || 'Sun';

  return (
    <div className="bg-card rounded-xl shadow-lg border border-border p-6">
      <h3 className="text-lg font-semibold text-foreground mb-4">Weekly Schedule</h3>
      <div className="overflow-x-auto">
        <div className="schedule-grid min-w-[600px]">
          <div className="schedule-time-slot font-medium text-muted-foreground">Time</div>
          {days?.map((day) =>
          <div key={day} className="schedule-time-slot font-medium text-foreground">
              {day}
            </div>
          )}
          
          {hours?.map((hour) =>
          <React.Fragment key={hour}>
              <div className="schedule-time-slot text-muted-foreground">{hour}</div>
              {days?.map((day) => {
              const isCurrentSlot = day === currentDay && parseInt(hour) === currentHour;
              const hasSchedule = scheduleForChannel?.some((s) =>
              s?.day === day && s?.start_time <= hour && s?.end_time > hour
              );

              return (
                <div
                  key={`${day}-${hour}`}
                  className={`schedule-time-slot ${isCurrentSlot ? 'schedule-live-slot' : ''} ${
                  hasSchedule ? 'bg-primary/10 text-primary' : ''}`
                  }>
                    {isCurrentSlot &&
                  <div className="absolute top-1 right-1 w-2 h-2 bg-red-600 rounded-full live-indicator"></div>
                  }
                    {hasSchedule &&
                  <span className="text-xs">
                        {mockShows?.find((s) =>
                    scheduleForChannel?.find((sch) => sch?.show_id === s?.id && sch?.day === day)
                    )?.title?.slice(0, 8)}
                      </span>
                  }
                  </div>);

            })}
            </React.Fragment>
          )}
        </div>
      </div>
    </div>);


















};

WeeklySchedule.propTypes = {
  channelId: PropTypes?.number?.isRequired
};

// Main Component
export default function LiveStreamsHub() {
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTags, setSelectedTags] = useState([]);
  const [showLiveOnly, setShowLiveOnly] = useState(false);
  const [currentAudioChannel, setCurrentAudioChannel] = useState(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [selectedVideoChannel, setSelectedVideoChannel] = useState(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [selectedChannelDetail, setSelectedChannelDetail] = useState(null);
  const [showTrackHistory, setShowTrackHistory] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [isTagsDropdownOpen, setIsTagsDropdownOpen] = useState(false);

  // Get unique categories and tags
  const categories = ['all', ...new Set(mockChannels.map((c) => c.category))];
  const allTags = [...new Set(mockChannels.flatMap((c) => c.tags || []))];

  // Filter channels
  const filteredChannels = mockChannels?.filter((channel) => {
    if (activeTab !== 'all' && channel?.type !== activeTab) return false;
    if (showLiveOnly && !channel?.is_live) return false;
    if (selectedCategory !== 'all' && channel?.category !== selectedCategory) return false;
    if (selectedTags?.length > 0 && !selectedTags?.some((tag) => channel?.tags?.includes(tag))) return false;
    if (searchQuery) {
      return (
        channel?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        channel?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        channel?.tags?.some((tag) => tag?.toLowerCase()?.includes(searchQuery?.toLowerCase())));

    }
    return true;
  });

  const handlePlay = (channel) => {
    if (channel?.type === 'radio') {
      setCurrentAudioChannel(channel);
      setIsAudioPlaying(true);
    } else if (channel?.type === 'tv') {
      setSelectedVideoChannel(channel);
      setIsVideoModalOpen(true);
    }
  };

  const handleToggleAudioPlay = () => {
    setIsAudioPlaying(!isAudioPlaying);
  };

  const handleCloseAudioPlayer = () => {
    setCurrentAudioChannel(null);
    setIsAudioPlaying(false);
  };

  const handleCloseVideoModal = () => {
    setIsVideoModalOpen(false);
    setSelectedVideoChannel(null);
  };

  const handleOpenDetail = (channel) => {
    setSelectedChannelDetail(channel);
  };

  const handleTagToggle = (tag) => {
    setSelectedTags((prev) =>
    prev?.includes(tag) ? prev?.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setIsCategoryDropdownOpen(false);
  };

  const handleTagSelect = (tag) => {
    handleTagToggle(tag);
    // Note: We don't close the dropdown here so users can select multiple tags
  };

  const clearAllTags = () => {
    setSelectedTags([]);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="pt-20 pb-8 bg-gradient-to-br from-primary/5 via-transparent to-accent/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-foreground mb-4">
              Live Streams Hub
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Discover and enjoy Christian radio stations, TV channels, and live streaming content from around the world
            </p>
          </div>

          {/* Search and Filters */}
          <div className="bg-card rounded-xl shadow-lg border border-border p-6 mb-8">
            <div className="flex flex-col lg:flex-row gap-4 mb-6">
              {/* Search */}
              <div className="flex-1 relative">
                <Icon name="MagnifyingGlassIcon" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search channels, shows, categories..."
                  className="w-full pl-10 pr-4 py-3 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)} />

              </div>

              {/* Live Only Toggle */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showLiveOnly}
                  onChange={(e) => setShowLiveOnly(e?.target?.checked)}
                  className="w-5 h-5 text-primary border-border rounded focus:ring-primary" />

                <span className="text-foreground font-medium">Live Only</span>
                <div className="w-2 h-2 bg-red-600 rounded-full live-indicator"></div>
              </label>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-2 mb-4">
              {[
              { id: 'all', label: 'All Channels', icon: 'Squares2X2Icon' },
              { id: 'radio', label: 'Radio', icon: 'SpeakerWaveIcon' },
              { id: 'tv', label: 'TV', icon: 'VideoCameraIcon' }]?.
              map((tab) =>
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium contemplative-transition ${
                activeTab === tab?.id ?
                'bg-primary text-primary-foreground' :
                'bg-surface text-muted-foreground hover:text-foreground hover:bg-background'}`
                }>

                  <Icon name={tab?.icon} size={16} />
                  {tab?.label}
                </button>
              )}
            </div>

            {/* Dropdown Filters Container */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              {/* Category Filter - Mobile-Friendly Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                  className="flex items-center justify-between w-full px-4 py-3 bg-surface border border-border rounded-lg text-foreground hover:bg-background contemplative-transition">

                  <div className="flex items-center gap-2">
                    <Icon name="FunnelIcon" size={16} className="text-muted-foreground" />
                    <span className="font-medium">
                      {selectedCategory === 'all' ? 'All Categories' : selectedCategory}
                    </span>
                  </div>
                  <Icon
                    name="ChevronDownIcon"
                    size={16}
                    className={`text-muted-foreground contemplative-transition ${
                    isCategoryDropdownOpen ? 'rotate-180' : ''}`
                    } />

                </button>

                {isCategoryDropdownOpen &&
                <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                    <div className="py-2">
                      {categories?.map((category) =>
                    <button
                      key={category}
                      onClick={() => handleCategorySelect(category)}
                      className={`w-full text-left px-4 py-3 hover:bg-surface contemplative-transition flex items-center justify-between ${
                      selectedCategory === category ? 'bg-primary/10 text-primary font-medium' : 'text-foreground'}`
                      }>

                          <span>{category === 'all' ? 'All Categories' : category}</span>
                          {selectedCategory === category &&
                      <Icon name="CheckIcon" size={16} className="text-primary" />
                      }
                        </button>
                    )}
                    </div>
                  </div>
                }
              </div>

              {/* Tags Filter - Mobile-Friendly Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsTagsDropdownOpen(!isTagsDropdownOpen)}
                  className="flex items-center justify-between w-full px-4 py-3 bg-surface border border-border rounded-lg text-foreground hover:bg-background contemplative-transition">

                  <div className="flex items-center gap-2">
                    <Icon name="TagIcon" size={16} className="text-muted-foreground" />
                    <span className="font-medium">
                      {selectedTags?.length === 0 ?
                      'All Tags' :
                      selectedTags?.length === 1 ?
                      selectedTags?.[0] :
                      `${selectedTags?.length} Tags Selected`
                      }
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedTags?.length > 0 &&
                    <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
                        {selectedTags?.length}
                      </span>
                    }
                    <Icon
                      name="ChevronDownIcon"
                      size={16}
                      className={`text-muted-foreground contemplative-transition ${
                      isTagsDropdownOpen ? 'rotate-180' : ''}`
                      } />

                  </div>
                </button>

                {isTagsDropdownOpen &&
                <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                    <div className="py-2">
                      {/* Clear All Tags Option */}
                      {selectedTags?.length > 0 &&
                    <>
                          <button
                        onClick={clearAllTags}
                        className="w-full text-left px-4 py-3 hover:bg-surface contemplative-transition flex items-center justify-between text-orange-600 font-medium">

                            <span>Clear All Tags</span>
                            <Icon name="XMarkIcon" size={16} />
                          </button>
                          <div className="border-b border-border mx-2"></div>
                        </>
                    }
                      
                      {/* All Tags List */}
                      {allTags?.map((tag) =>
                    <button
                      key={tag}
                      onClick={() => handleTagSelect(tag)}
                      className={`w-full text-left px-4 py-3 hover:bg-surface contemplative-transition flex items-center justify-between ${
                      selectedTags?.includes(tag) ? 'bg-primary/10 text-primary font-medium' : 'text-foreground'}`
                      }>

                          <span>{tag}</span>
                          {selectedTags?.includes(tag) &&
                      <Icon name="CheckIcon" size={16} className="text-primary" />
                      }
                        </button>
                    )}
                    </div>
                  </div>
                }
              </div>
            </div>

            {/* Filter Summary */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Icon name="AdjustmentsHorizontalIcon" size={14} />
                <span>Active filters:</span>
              </div>
              
              <div className="flex items-center gap-2">
                <span>Category: {selectedCategory === 'all' ? 'All' : selectedCategory}</span>
              </div>
              
              {selectedTags?.length > 0 &&
              <div className="flex items-center gap-2">
                  <span>Tags: {selectedTags?.join(', ')}</span>
                </div>
              }
              
              {(selectedCategory !== 'all' || selectedTags?.length > 0) &&
              <button
                onClick={() => {
                  setSelectedCategory('all');
                  setSelectedTags([]);
                }}
                className="text-primary hover:text-primary/80 contemplative-transition font-medium">

                  Reset Filters
                </button>
              }
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-32">
        {/* Results Summary */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-muted-foreground">
            Found {filteredChannels?.length} channel{filteredChannels?.length !== 1 ? 's' : ''}
            {activeTab !== 'all' && ` in ${activeTab?.toUpperCase()}`}
            {showLiveOnly && ' currently live'}
          </p>
        </div>

        {/* Channel Grid */}
        {filteredChannels?.length === 0 ?
        <div className="text-center py-16">
            <Icon name="MagnifyingGlassIcon" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No channels found</h3>
            <p className="text-muted-foreground">
              Try adjusting your filters or search terms
            </p>
          </div> :

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredChannels?.map((channel) =>
          <ChannelCard
            key={channel?.id}
            channel={channel}
            onPlay={handlePlay}
            onOpenDetail={handleOpenDetail} />

          )}
          </div>
        }

        {/* Channel Detail Panel */}
        {selectedChannelDetail &&
        <div className="mt-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-foreground">Channel Details</h2>
              <button
              onClick={() => setSelectedChannelDetail(null)}
              className="p-2 text-muted-foreground hover:text-foreground contemplative-transition">

                <Icon name="XMarkIcon" size={24} />
              </button>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              <div className="bg-card rounded-xl shadow-lg border border-border p-6">
                <div className="flex items-start gap-4 mb-6">
                  <Image
                  src={selectedChannelDetail?.logo_url}
                  alt={selectedChannelDetail?.alt}
                  width={80}
                  height={80}
                  className="rounded-lg" />

                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      {selectedChannelDetail?.title}
                    </h3>
                    <p className="text-muted-foreground mb-3">
                      {selectedChannelDetail?.description}
                    </p>
                    <div className="flex items-center gap-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    selectedChannelDetail?.type === 'radio' ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'}`
                    }>
                        {selectedChannelDetail?.type?.toUpperCase()}
                      </span>
                      {selectedChannelDetail?.is_live &&
                    <div className="flex items-center gap-2 text-red-600 text-sm font-medium">
                          <div className="w-2 h-2 bg-red-600 rounded-full live-indicator"></div>
                          LIVE
                        </div>
                    }
                    </div>
                  </div>
                </div>

                {selectedChannelDetail?.is_live && selectedChannelDetail?.now_playing &&
              <div className="mb-6 p-4 bg-surface rounded-lg">
                    <h4 className="font-medium text-foreground mb-2">Now Playing</h4>
                    <p className="text-foreground mb-1">{selectedChannelDetail?.now_playing?.title}</p>
                    <p className="text-sm text-muted-foreground">
                      {selectedChannelDetail?.now_playing?.show} • {selectedChannelDetail?.now_playing?.host}
                    </p>
                  </div>
              }

                <button
                onClick={() => handlePlay(selectedChannelDetail)}
                className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg hover:bg-primary/90 contemplative-transition">

                  <Icon name="PlayIcon" size={20} />
                  Play Channel
                </button>
              </div>

              <WeeklySchedule channelId={selectedChannelDetail?.id} />
            </div>
          </div>
        }
      </div>
      {/* Sticky Audio Player */}
      <StickyPlayer
        currentChannel={currentAudioChannel}
        isPlaying={isAudioPlaying}
        onTogglePlay={handleToggleAudioPlay}
        onClose={handleCloseAudioPlayer} />
      {/* Video Modal */}
      <VideoModal
        channel={selectedVideoChannel}
        isOpen={isVideoModalOpen}
        onClose={handleCloseVideoModal} />
      {/* Close dropdowns when clicking outside */}
      {(isCategoryDropdownOpen || isTagsDropdownOpen) &&
      <div
        className="fixed inset-0 z-40"
        onClick={() => {
          setIsCategoryDropdownOpen(false);
          setIsTagsDropdownOpen(false);
        }} />

      }
    </div>);

}